
import { useState, useEffect } from 'react';

interface Stat {
  icon: string;
  value: string;
  label: string;
  color: string;
}

export default function StatisticsBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('statistics-banner');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const stats: Stat[] = [
    {
      icon: 'ri-user-heart-line',
      value: '2,500+',
      label: 'Clients Served',
      color: 'text-secondary'
    },
    {
      icon: 'ri-building-line',
      value: '20+',
      label: 'Insurance Carriers',
      color: 'text-secondary'
    },
    {
      icon: 'ri-time-line',
      value: '15+',
      label: 'Years in Business',
      color: 'text-secondary'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      value: '$3.2M+',
      label: 'Average Annual Savings',
      color: 'text-secondary'
    }
  ];

  return (
    <section id="statistics-banner" className="py-20 bg-gradient-to-br from-primary to-primary/90 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Trusted by Dallas Families &amp; Businesses
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Our track record speaks for itself — delivering exceptional health insurance solutions across Dallas-Fort Worth
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center transition-all duration-700 ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all duration-300 border border-white/20">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
                  <i className={`${stat.icon} text-4xl text-white`}></i>
                </div>
                <div className="text-5xl font-bold text-white mb-3">
                  {stat.value}
                </div>
                <div className="text-lg text-white/90 font-medium">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto border border-white/20">
            <h3 className="font-heading text-2xl font-bold text-white mb-4">
              Ready to Join Our Growing Family of Satisfied Clients?
            </h3>
            <p className="text-white/90 mb-6 text-lg">
              Experience the difference of working with Dallas's most trusted independent health insurance agency
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+12143060121"
                className="px-8 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>(214) 306-0121</span>
              </a>
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/contact');
                }}
                className="px-8 py-4 bg-white text-primary rounded-lg font-semibold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
              >
                <i className="ri-message-3-line text-xl"></i>
                <span>Get Free Quote</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
